import React, { useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { useQuery } from '@tanstack/react-query'
import { adminQueries } from '../../../api/admin'
import { adminSearchUserSchema } from '../../../forms/schemas/admin_tools'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { theme } from '../../../theme/theme'
import { Button, ButtonVariant } from '../../common/Button'
import { IconChoices } from '../../common/Icon'
import { SearchBar } from '../../common/SearchBar'

interface AddFeatureFlagSocietyProps {
  societyIdsWithFlag: string[]
  onAdd: (societyId: string) => void
  disabled: boolean
}

export const AddFeatureFlagSociety = ({
  societyIdsWithFlag,
  onAdd,
  disabled,
}: AddFeatureFlagSocietyProps): JSX.Element => {
  const { translate } = useAppTranslation()
  const [query, setQuery] = useState('')
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(adminSearchUserSchema),
    defaultValues: { query: '' },
  })

  const { data: matches, isFetching } = useQuery(
    adminQueries.searchSociety(query)
  )

  const onSubmit = (data: InferType<typeof adminSearchUserSchema>): void => {
    setQuery(data.query.trim())
  }

  return (
    <div className="flex flex-col gap-2">
      <form className="flex items-end gap-1" onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="query"
          control={control}
          render={({ field: { value, onChange } }) => (
            <SearchBar
              placeholder={translate(
                'adminView.searchBar.placeholders.societies'
              )}
              value={value}
              onChange={onChange}
            />
          )}
        />
        <Button
          type="submit"
          className="max-h-12"
          loading={isFetching}
          disabled={isFetching || !isValid}
          icon={IconChoices.SEARCH}
        />
      </form>
      {matches?.length === 0 && (
        <p style={theme.textVariants.base}>
          {translate('adminView.searchResult.emptyResult')}
        </p>
      )}
      {matches?.map((society) => (
        <div
          key={society._id}
          className="flex items-center justify-between gap-4"
        >
          <div>
            <p style={theme.textVariants.baseBold}>{society.name}</p>
            <p style={theme.textVariants.caption}>
              {[society.addressCity, society.organisationNumber]
                .filter((part) => part != null && part.length > 0)
                .join(' · ')}
            </p>
          </div>
          {!societyIdsWithFlag.includes(society._id) && (
            <Button
              size="sm"
              variant={ButtonVariant.PRIMARY}
              label={translate('adminView.featureFlags.add')}
              disabled={disabled}
              onClick={() => onAdd(society._id)}
            />
          )}
        </div>
      ))}
    </div>
  )
}
